<template>
  <div class="signup-verify signup-atmosphere">
    <CloudLayer />

    <header class="signup-verify__topbar signup-shell-content">
      <BrandMark />
    </header>

    <main class="signup-verify__main signup-shell-content">
      <section class="signup-verify__card signup-card signup-fade-up">
        <div class="signup-verify__header signup-stagger">
          <div class="signup-eyebrow">Verify your email</div>
          <h1 class="signup-display signup-display--sm">
            Check your
            <b>email</b>.
          </h1>
          <p class="signup-subtitle">
            We sent a 4-digit code to
            <strong class="signup-verify__email">{{ email || 'your work email' }}</strong>.
            It expires in 5 minutes.
          </p>
        </div>

        <div class="signup-verify__pin">
          <PinInput
            :value="pin"
            :length="4"
            :loading="verifying"
            :error="errorMessage"
            @input="onPinInput"
            @complete="onPinComplete"
          />
        </div>

        <p class="signup-verify__resend">
          Didn't get it?
          <button
            type="button"
            class="signup-verify__resend-btn"
            :disabled="resending || verifying"
            @click="onResend"
          >
            {{ resending ? 'Sending…' : 'Resend code' }}
          </button>
          <span class="signup-verify__resend-sep">·</span>
          <router-link to="/signup" class="signup-verify__resend-btn signup-verify__resend-btn--link">
            Start over
          </router-link>
          <span v-if="resendMessage" class="signup-verify__resend-msg">
            {{ resendMessage }}
          </span>
        </p>
      </section>
    </main>
  </div>
</template>

<script>
import PinInput from './PinInput.vue'
import BrandMark from './BrandMark.vue'
import CloudLayer from './CloudLayer.vue'

export default {
  name: 'SignupPinVerify',
  components: { PinInput, BrandMark, CloudLayer },
  data() {
    return {
      pin: '',
      verifying: false,
      resending: false,
      errorMessage: '',
      resendMessage: '',
    }
  },
  computed: {
    email() {
      return (this.$store.state.signup && this.$store.state.signup.email) || ''
    },
    pinRequested() {
      return !!(this.$store.state.signup && this.$store.state.signup.pinRequested)
    },
    // Best-effort — the entry step seeds wizardData.company.name before
    // navigating here. If a user lands on /signup/verify directly we fall
    // back to '' (see resend path below).
    storedCompanyName() {
      const s = this.$store.state.signup
      if (!s || !s.wizardData || !s.wizardData.company) return ''
      return s.wizardData.company.name || ''
    },
  },
  created() {
    // Route guard — Track A1 scaffolding allows /signup/verify regardless
    // of state; enforce here that requestPin was called first.
    if (!this.pinRequested) {
      this.$router.replace('/signup')
    }
  },
  methods: {
    onPinInput(val) {
      this.pin = val
      // Clear inline error as soon as the user edits again.
      if (this.errorMessage) this.errorMessage = ''
    },
    async onPinComplete(val) {
      if (this.verifying) return
      this.pin = val
      this.errorMessage = ''
      this.verifying = true
      try {
        await this.$store.dispatch('signup/verifyPin', { pin: val })
        this.$router.push('/signup/wizard/company')
      } catch (err) {
        this.errorMessage = "That code doesn't look right. Try again or resend."
        this.pin = ''
      } finally {
        this.verifying = false
      }
    },
    async onResend() {
      if (this.resending || !this.email) return
      this.errorMessage = ''
      this.resendMessage = ''
      this.resending = true
      try {
        // TODO: if storedCompanyName is empty (user deep-linked to /verify),
        // we pass '' — the backend's request-pin accepts an optional
        // companyName. Track B2's Step 1 will re-capture the name anyway.
        await this.$store.dispatch('signup/requestPin', {
          email: this.email,
          companyName: this.storedCompanyName || '',
        })
        this.resendMessage = 'Sent — check your inbox.'
        this.pin = ''
      } catch (err) {
        this.resendMessage = ''
        this.errorMessage =
          (err && err.message) ||
          "Couldn't resend right now. Try again in a moment."
      } finally {
        this.resending = false
        // Hide the confirmation after a few seconds.
        if (this.resendMessage) {
          setTimeout(() => { this.resendMessage = '' }, 4000)
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import './_shared.scss';

.signup-verify {
  display: flex;
  flex-direction: column;
}

/* Centered brand mark — same intent as SignupEntry: pre-wizard pages let
   the brand carry the moment, then it shifts to the corner once the
   wizard's progress indicator takes the centered slot. */
.signup-verify__topbar {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px 32px;

  @media (max-width: 640px) { padding: 16px 20px; }
}

.signup-verify__main {
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 24px 24px 64px;

  @media (max-width: 640px) { padding: 12px 16px 40px; }
}

.signup-verify__card {
  width: 100%;
  max-width: 480px;
  padding: 40px 36px 32px;

  @media (max-width: 640px) {
    padding: 28px 22px 24px;
    border-radius: 14px;
  }
}

.signup-verify__header {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 28px;
  text-align: left;
}

.signup-verify__email {
  color: var(--signup-text);
  font-weight: 800;
}

.signup-verify__pin {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
}

.signup-verify__resend {
  text-align: center;
  font-size: 13px;
  color: var(--signup-text-muted);
  margin-top: 22px;
  line-height: 1.5;
}
.signup-verify__resend-sep {
  margin: 0 6px;
  color: var(--signup-text-muted);
  opacity: 0.5;
}
.signup-verify__resend-btn--link {
  text-decoration: none;
}

.signup-verify__resend-btn {
  appearance: none;
  background: transparent;
  border: none;
  padding: 0;
  font: inherit;
  color: var(--signup-brand);
  font-weight: 800;
  cursor: pointer;

  &:hover { text-decoration: underline; }
  &:disabled {
    color: var(--signup-text-muted);
    cursor: not-allowed;
    text-decoration: none;
  }
  &:focus { outline: none; }
  &:focus-visible {
    outline: 2px solid var(--signup-gold);
    outline-offset: 3px;
    border-radius: 4px;
  }
}

.signup-verify__resend-msg {
  flex-basis: 100%;
  text-align: center;
  color: var(--signup-success);
  font-weight: 700;
  font-size: 12px;
  margin-top: 2px;
}
</style>
